import { SameLocationInfo, SameLocationTarget } from 'src/components/dispatch/unassignedDisplay/sameLocationVM';
import { DispatchCard, UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';

import { buildDisplayRotationNumbers, VehicleRowGroup } from './adapter';
import { estimateHubPlaceKeys, isDispatchedCard } from './estimateHubPlaceKeys';
import { isMainWarehouseSide, PlaceSide, placeKeyOf } from './placeKeys';

/**
 * 未割当案件ごとに「同じ地点がすでに他車両へ割当済みか」を判定する
 * (logposeTech/log_web_issues#318)。
 *
 * 地点の同一性は `placeKeys.ts` の `placeKeyOf` だけで決める。配車済みカードは
 * `DispatchCard.placeKey` に同じ関数の結果を持っているので、こちらは未割当案件側の
 * キーを同じ関数で作って突き合わせるだけにする。
 *
 * 突合は積み降ろしを問わない (2026-09-03 の拡張)。未割当案件の積地・降地の双方を、
 * 配車済みカードの積地・降地の双方と突き合わせる (クロス含む 4 方向)。同じ建物で
 * 集荷と配達が分かれている地点を拾うため。`placeKeyOf` は side をキーに含めないので、
 * 同一座標なら積地キーと降地キーが同じ文字列になり、キーの変更なしにクロスで一致する。
 *
 * 拠点 (配送センター・共同物流倉庫) は両側で判定から外す。積みも降ろしも大量に発生し、
 * 警告がノイズになる (dev 実測でタグ 37 件のうち 35 件が 1 拠点由来)。拠点かどうかは
 * 主要倉庫フラグ (places.is_main_warehouse) と当日データからの推定
 * (`estimateHubPlaceKeys`) の **OR** で決める。理由はそちらのファイル冒頭に書いた。
 *
 * Why not 既存バッジ (duplicateStoreNames) と同じ storeName 一致:
 * 同名で住所の違う拠点が合流するうえ、実データでは逆に「同一座標だが名称が違う」
 * ケースが多く取りこぼす (2025-07-07 の実測で未割当 1342 件のうち placeKey 判定 112 件に対し
 * storeName 判定は 29 件。差の 83 件はすべて座標一致・名称違い)。
 *
 * Why not 判定を配車済みカードどうしにも広げる: 既存の「他車両での取扱あり」バッジと
 * 詳細パネルの警告は storeName 判定のまま据え置く (#318 のスコープ外)。
 */
export function buildSameLocationAssignments(
  items: readonly UnassignedItem[],
  rowGroups: readonly VehicleRowGroup[],
  grouping: DeliveryListGroupingValues,
): Map<string, SameLocationInfo> {
  const result = new Map<string, SameLocationInfo>();
  if (items.length === 0 || rowGroups.length === 0) return result;

  const hubPlaceKeys = estimateHubPlaceKeys(items, rowGroups, grouping);
  const assigned = collectAssignedTargets(rowGroups, hubPlaceKeys);
  if (assigned.size === 0) return result;

  for (const item of items) {
    const targets = collectTargetsForItem(item, assigned, grouping, hubPlaceKeys);
    if (targets.length > 0) result.set(item.id, { targets });
  }

  return result;
}

/**
 * 突き合わせる側。降地を先に見るのは、#318 当初から降地だけで出していた並び順を保つため
 * (ツールチップの行順が変わると「増えた」以上の差分に見える)。
 */
const JUDGED_SIDES: readonly PlaceSide[] = ['unloading', 'loading'];

/**
 * 1 案件ぶんの相手を集める。積地・降地の両側で突き合わせて和集合を取り、
 * 同じ相手を 2 回数えないよう `dedupeKeyOf` で重複排除する
 * (積地と降地が同座標の legRun では 2 本のキーが同じ文字列になる)。
 */
function collectTargetsForItem(
  item: UnassignedItem,
  assigned: Map<string, SameLocationTarget[]>,
  grouping: DeliveryListGroupingValues,
  hubPlaceKeys: ReadonlySet<string>,
): SameLocationTarget[] {
  const targets: SameLocationTarget[] = [];
  const seen = new Set<string>();

  for (const side of JUDGED_SIDES) {
    // fallback に item.id を渡すのは、座標も名称も無い legRun を誰とも一致させないため。
    // カード側の fallback は `op-{operationId}` なので、どちらの一意値とも衝突しない
    const placeKey = placeKeyOf(side, item.source, item.id, grouping);

    // 拠点は判定から外す。マスタのフラグと当日データからの推定の OR。
    // どちらでも拠点でなければ外さない (タグが増える向きに倒す)
    if (isMainWarehouseSide(side, item.source) || hubPlaceKeys.has(placeKey)) continue;

    const found = assigned.get(placeKey);
    if (!found) continue;

    for (const target of found) {
      const key = dedupeKeyOf(target);
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push(target);
    }
  }

  return targets;
}

/**
 * レーン (地点単位表示) 1 行ぶんの情報にまとめる。
 *
 * レーンは同一地点の伝票をまとめたグループなので、配下 5 伝票が同じ 1 台を指す。
 * 素朴に連結すると「割当済 (5)」になり、読み手が台数と取り違える。
 * 重複排除の単位は伝票カードと同じ 行 (車両 × 回転) × 地点名。
 *
 * Why not サンプルのように先頭 1 件だけ拾う: レーンキーは積地→降地のペアなので
 * 降地は同一だが、配下の伝票ごとに割当済みの相手が違いうる (座標が同じで名称の違う
 * 地点が実データに存在する)。先頭だけだと他の相手が落ちる。
 */
export function mergeSameLocationInfos(
  infos: readonly (SameLocationInfo | undefined)[],
): SameLocationInfo | undefined {
  const targets: SameLocationTarget[] = [];
  const seen = new Set<string>();

  for (const info of infos) {
    if (!info) continue;
    for (const target of info.targets) {
      const key = dedupeKeyOf(target);
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push(target);
    }
  }

  return targets.length > 0 ? { targets } : undefined;
}

/**
 * 重複排除の単位。行 (= 車両 × 回転) と地点名で決める。
 * 地点名まで見るのは、同じ行が同一座標の別名称カードを 2 枚持つとき
 * （実データにある）に両方の名前を見せたいため。
 */
const dedupeKeyOf = (t: SameLocationTarget): string => `${t.rowId}-${t.storeName}`;

/** ツールチップに出す地点名。積地カードは storeName を持たないので locationName を読む */
const storeNameOf = (card: DispatchCard): string => (
  card.type === 'delivery' ? card.storeName : card.locationName
) ?? '';

/** placeKey → 割当済みの訪問。行 × 地点名で重複排除する */
function collectAssignedTargets(
  rowGroups: readonly VehicleRowGroup[],
  hubPlaceKeys: ReadonlySet<string>,
): Map<string, SameLocationTarget[]> {
  const byPlaceKey = new Map<string, SameLocationTarget[]>();
  const seen = new Map<string, Set<string>>();

  for (const group of rowGroups) {
    // 回転番号は車両リストのバッジと同じ導出にする。生の rotationNumber だと
    // 回転追加の仮想行と FR placeholder の非表示でバッジとずれる
    const rotationNumbers = buildDisplayRotationNumbers(group.rows);

    group.rows.forEach((row, index) => {
      for (const card of row.cards) {
        // 固定ルート由来の仮カードは割当済みに数えない (判定理由は isDispatchedCard)。
        // LoadingCard は status を持たないが、積地カードは実 operation 由来しか無いので
        // 取りこぼしは起きない (固定ルートの stop は template / no_delivery になり
        // buildDeliveryCard へ回る)
        if (!isDispatchedCard(card)) continue;
        if (!card.placeKey) continue;
        // 拠点は判定から外す (積地カード・降地カードの両方)。
        //
        // Why 推定もここに書く: 推定は placeKey だけで決まるので、未割当側で同じキーを
        // 外していれば結果は変わらない (引く前に continue するため)。それでも両側に
        // 置くのは、フラグの側が非対称だから — フラグは未割当側が legRun 単位、カード側は
        // マージされた legRun の OR で、カード側だけが拾える拠点がある。片側だけ推定を
        // 書くと「なぜこちらだけ」の説明が要る非対称がもう 1 つ増える
        if (card.isMainWarehouse || hubPlaceKeys.has(card.placeKey)) continue;

        const target: SameLocationTarget = {
          rowId: row.rowId,
          vehicleId: group.vehicle.id,
          vehicleNumber: group.vehicle.vehicleNumber,
          rotationNumber: rotationNumbers[index],
          storeName: storeNameOf(card),
        };

        const dedupeKey = dedupeKeyOf(target);
        const seenForPlace = seen.get(card.placeKey) ?? new Set<string>();
        if (seenForPlace.has(dedupeKey)) continue;
        seenForPlace.add(dedupeKey);
        seen.set(card.placeKey, seenForPlace);

        const bucket = byPlaceKey.get(card.placeKey);
        if (bucket) bucket.push(target);
        else byPlaceKey.set(card.placeKey, [target]);
      }
    });
  }

  return byPlaceKey;
}
